import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/common";
import { AssigneePicker, ResponsabiliPicker } from "@/components/OrderFormDialog";
import { useRecurringGuard } from "@/components/RecurringGuard";
import { api, errMsg } from "@/lib/api";
import { IMPEDIMENTS, toLocalInput } from "@/lib/format";

export const ACTIONS = {
  take: { label: "Prendi in carico", direct: true, tone: "primary" },
  start: { label: "Inizia lavoro", direct: true, tone: "primary" },
  resume: { label: "Riprendi lavoro", direct: true, tone: "primary" },
  complete: { label: "Dichiara completato", tone: "primary", desc: "Il lavoro passerà al responsabile per la verifica." },
  approve: { label: "Approva e chiudi", tone: "primary", desc: "Confermi la verifica del lavoro eseguito." },
  assign: { label: "Assegna / riassegna", fields: ["assignees"], tone: "outline" },
  delegate: { label: "Affida a responsabili", fields: ["delegates", "note"], tone: "outline", desc: "I responsabili scelti dovranno assegnarlo a un operatore." },
  set_priority: { label: "Modifica priorità", fields: ["priority"], tone: "outline" },
  delay_reason: { label: "Motiva ritardo", fields: ["category", "note"], noteRequired: true, tone: "outline" },
  suspend: { label: "Sospendi", fields: ["category", "note"], noteRequired: true, tone: "outline", noNone: true },
  request_docs: { label: "Richiedi documentazione", fields: ["note"], noteRequired: true, tone: "outline" },
  reject: { label: "Respingi", fields: ["note"], noteRequired: true, tone: "destructive" },
  reopen: { label: "Riapri", fields: ["note"], noteRequired: true, tone: "outline" },
  change_due: { label: "Modifica scadenza", fields: ["due", "note"], noteRequired: true, tone: "outline", desc: "La scadenza originale resta registrata nello storico." },
  cancel: { label: "Annulla ordine", fields: ["note"], noteRequired: true, tone: "destructive", desc: "L'annullamento richiede una motivazione e resta tracciato." },
  comment: { label: "Aggiungi nota", fields: ["note"], noteRequired: true, tone: "ghost" },
};

export const ActionDialog = ({ order, action, onClose, onDone }) => {
  const cfg = ACTIONS[action];
  const [note, setNote] = useState("");
  const [category, setCategory] = useState(cfg?.noNone ? "materiale" : "nessuno");
  const [due, setDue] = useState(order.due_current ? toLocalInput(order.due_current) : "");
  const [assignees, setAssignees] = useState(order.assignee_ids || []);
  const [delegates, setDelegates] = useState(order.delegated_to || []);
  const [priority, setPriority] = useState(order.priority || "normale");
  const [busy, setBusy] = useState(false);
  const { guard, dialog } = useRecurringGuard();
  if (!cfg) return null;
  const fields = cfg.fields || ["note"];

  const submit = async (suspend = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/action`, { action, note, category, due: due ? new Date(due).toISOString() : null, assignee_ids: assignees, delegate_ids: delegates, priority, suspend_recurring: suspend });
      toast.success(`${cfg.label}: registrato`);
      onDone(data);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async () => {
    if (action !== "assign" || order.source === "ricorrenza") return submit(false);
    try { await guard(assignees, submit); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid={`action-dialog-${action}`}>
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold">{cfg.label}</DialogTitle>
          <DialogDescription>{order.code} · {order.title}{cfg.desc ? ` — ${cfg.desc}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {fields.includes("priority") && <div className="space-y-1.5"><Label>Priorità</Label><NativeSelect className="w-full" testid="action-priority" value={priority} onChange={setPriority}><option value="normale">Normale</option><option value="alta">Alta</option></NativeSelect></div>}
          {fields.includes("delegates") && <div className="space-y-1.5"><Label>Responsabili della commessa</Label><ResponsabiliPicker commessaId={order.commessa_id} value={delegates} onChange={setDelegates} testid="action-delegates" /></div>}
          {fields.includes("assignees") && <div className="space-y-1.5"><Label>Operatori</Label><AssigneePicker commessaId={order.commessa_id} value={assignees} onChange={setAssignees} testid="action-assignees" /></div>}
          {fields.includes("category") && (
            <div className="space-y-1.5"><Label>Causa</Label>
              <NativeSelect className="w-full" testid="action-category-select" value={category} onChange={setCategory}>
                {Object.entries(IMPEDIMENTS).filter(([k]) => !(cfg.noNone && k === "nessuno")).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </NativeSelect></div>
          )}
          {fields.includes("due") && <div className="space-y-1.5"><Label>Nuova scadenza</Label><Input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} data-testid="action-due-input" /></div>}
          <div className="space-y-1.5"><Label>{cfg.noteRequired ? "Motivazione *" : "Nota (facoltativa)"}</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} data-testid="action-note-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Annulla</Button>
          <Button disabled={busy || (cfg.noteRequired && note.trim().length < 3)} onClick={onConfirm} variant={cfg.tone === "destructive" ? "destructive" : "default"} className="rounded-xl font-bold" data-testid="action-confirm-button">Conferma</Button>
        </DialogFooter>
        {dialog}
      </DialogContent>
    </Dialog>
  );
};
